import {
  Alert,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Typography,
} from "@mui/material";
import { HotkeyCompliantDataGrid } from "components/common/dataGrid/HotkeyCompliantDataGrid";
import { Fetching } from "components/common/loading/fetching";
import { isFetchBaseQueryError } from "helpers";
import { useLabellingLocations } from "hooks/useLabellingLocations";
import { useTenantTranslation } from "hooks/formatters";
import { useTenant } from "hooks/settings";
import { useFormatNumber } from "src/utils/formatNumber";
import { useMemo, useState } from "react";
import {
  ReviewerStatsEntry,
  useGetLabellerCostStatsQuery,
} from "src/store/api/generatedApi";
import { DateRangeFilterBar, useDateRangeFilters } from "./dateRangeFilterBar";
import { CopyWorkerStatsToClipboardButton } from "./copyStatsToClipboardButton";
import {
  buildReviewerBaseColumns,
  buildReviewerBaseCsvColumns,
  REVIEWER_NAME_FIELD,
  ReviewerBaseRow,
} from "./reviewerOverviewColumns";

const toRow = (entry: ReviewerStatsEntry): ReviewerBaseRow => ({
  ...entry,
  id: entry.reviewer.id,
});

export const ReviewerOverviewTab = () => {
  const { t } = useTenantTranslation();
  const tenantName = useTenant();
  const formatNumber1dp = useFormatNumber()(1);
  const dateFilters = useDateRangeFilters();
  const [location, setLocation] = useState("");

  const { locationNames } = useLabellingLocations();

  const { data, isLoading, isFetching, error } = useGetLabellerCostStatsQuery({
    tenantName,
    start: dateFilters.start?.toISOString() ?? undefined,
    end: dateFilters.end?.toISOString() ?? undefined,
    filterMode: dateFilters.filterMode,
    location: location || undefined,
  });

  const rows = useMemo(() => (data?.reviewer_stats ?? []).map(toRow), [data]);

  const columns = useMemo(
    () => buildReviewerBaseColumns(t, formatNumber1dp),
    [t, formatNumber1dp]
  );

  const csvColumns = useMemo(() => buildReviewerBaseCsvColumns(t), [t]);

  if (error) {
    const detail = isFetchBaseQueryError(error)
      ? typeof error.data === "string"
        ? error.data
        : JSON.stringify(error.data)
      : (error as { message?: string }).message;

    return (
      <Alert severity="error">
        {t("unknownError")}
        {detail ? (
          <Typography variant="body2" sx={{ mt: 1 }}>
            {detail}
          </Typography>
        ) : null}
      </Alert>
    );
  }

  return (
    <Stack spacing={2} sx={{ flex: 1, overflow: "hidden" }}>
      <Stack direction="row" spacing={2} alignItems="center" sx={{ pt: 1 }}>
        <DateRangeFilterBar filters={dateFilters} />
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel id="reviewer-location-filter-label">
            {t("location")}
          </InputLabel>
          <Select
            labelId="reviewer-location-filter-label"
            value={location}
            label={t("location")}
            onChange={(e) => {
              setLocation(e.target.value);
            }}
          >
            <MenuItem value="">{t("all")}</MenuItem>
            {locationNames.map((name) => (
              <MenuItem key={name} value={name}>
                {name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <CopyWorkerStatsToClipboardButton rows={rows} columns={csvColumns} />
      </Stack>
      <Fetching fetching={isFetching} sx={{ flex: 1, overflow: "hidden" }}>
        <HotkeyCompliantDataGrid
          columns={columns}
          rows={rows}
          loading={isLoading}
          initialState={{
            sorting: {
              sortModel: [{ field: REVIEWER_NAME_FIELD, sort: "asc" }],
            },
          }}
          slotProps={{
            loadingOverlay: { variant: "skeleton", noRowsVariant: "skeleton" },
          }}
          rowHeight={40}
          columnHeaderHeight={44}
          disableRowSelectionOnClick
        />
      </Fetching>
    </Stack>
  );
};
